import { deepEqual } from '@flowus/common';
import { cx } from '@flowus/common/cx';
import { BlockStatus, BlockType, PermissionRole } from '@next-space/fe-api-idl';
import { pick, throttle } from 'lodash-es';
import type { FC } from 'react';
import React, { memo, useEffect, useMemo, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { Icon } from 'src/common/components/icon';
import { Tooltip } from 'src/common/components/tooltip';
import { VisibleRender } from 'src/common/components/visible-render';
import { RenderMode } from 'src/common/components/visible-render/types';
import { ILLEGAL_TEXT } from 'src/common/const';
import { BlockDefaultIcon } from 'src/components/block-default-icon';
import { useOpenFilePreview } from 'src/components/file-preview/use-open-file-preview';
import { IconTrigger } from 'src/components/icon-trigger';
import { IconUiSizeBox } from 'src/components/icon-ui-size-box';
import { useTocListOnSelect } from 'src/components/move-to/toc-list';
import { NoAccess } from 'src/components/no-access';
import { segmentsToText } from 'src/editor/utils/editor';
import { useFetchPage, useNormalizePath, useReadonly, useSubPages } from 'src/hooks/page';
import { isAsideArea } from 'src/hooks/page/use-dnd/helper';
import { useIsDragging } from 'src/hooks/page/use-dnd/hooks';
import { useOpenPage } from 'src/hooks/page/use-open-page';
import { usePermissions } from 'src/hooks/share/use-permissions';
import { setExpandPageRecord, useExpandPageRecord } from 'src/services/app';
import { $dropInfo } from 'src/services/app/hook/use-drop-info';
import { useObservableStore } from 'src/services/rxjs-redux/hook';
import { useGetSyncService } from 'src/services/sync';
import { bizTracker } from 'src/utils/biz-tracker';
import { isLinkPageBlock, isLinkTableBlock } from 'src/utils/block-type-utils';
import { getFileNameInfo } from 'src/utils/file';
import { getUntitledName } from 'src/utils/get-untitled-name';
import { judgeSharePage } from 'src/utils/getPageId';
import { usePickBlock } from 'src/utils/pick-block';
import { bindDataTestId, TestIds } from 'src/utils/qa-utils';
import { getRightPageId } from 'src/utils/right-utils';
import { useInAside } from '../context';
import { AddButton } from './add-button';
import { SegmentPlainText } from './helper';
import { MoreButton, useOpenRightMenu } from './more-bottom';
import { NewPage } from './new-page';
import { useTocGroupType } from './toc-group-header';

interface Props {
  /** 页面 ID */
  uuid: string;
  deep: number;
  isInLinkPage?: boolean;
}

export const TocItem: FC<Props> = memo((props) => {
  const inAside = useInAside();

  if (!inAside) {
    return <TocItemContent key={props.uuid} {...props} />;
  }

  return (
    <VisibleRender blockId={props.uuid} defaultItemHeight={32} renderMode={RenderMode.innerHtml}>
      <TocItemContent key={props.uuid} {...props} />
    </VisibleRender>
  );
}, deepEqual);

const TocItemContent: FC<Props> = ({ uuid, deep, isInLinkPage }) => {
  const openPage = useOpenPage();
  const openRightMenu = useOpenRightMenu({ uuid, deep });
  const block = useTocItemBlock(uuid);
  const { role } = usePermissions(uuid);
  // 不要改这里，引用页面的isLinkPage变量为true
  const maybeLinkBlock = usePickBlock(uuid, ['data']);
  const subPages = useSubPages(block?.uuid);
  // uuid有可能是reference block,所以需要判断一下
  const isLinkPage = isLinkPageBlock(maybeLinkBlock);
  const isLinkTable = isLinkTableBlock(maybeLinkBlock);
  const isDragging = useIsDragging();
  const linkPagePath = useNormalizePath(block?.uuid ?? '');
  // const normalPath = useNormalizePath(uuid);
  /**
   * 关于expandId的使用:
   *
   * 因为新增了引用页面展示,在2个page互相引用的情况下存在套娃的现象(notion也一样能套娃)，单纯用uuid会有死循环问题
   * 如果当前是引用页面，我们用deep作为expandId的参数之一
   *
   * 另外收藏类型是独立存在的，展开不需要跟空间网页分类同步，因此收藏分类也需要独立的expandId
   * 总结:
   * 空间页面的expandId就是uuid，引用页面以及引用页面下的子页面就是uuid_deep
   * 收藏页面的expandId在空间页面的逻辑下，加上favorite前缀
   */
  const groupType = useTocGroupType();
  // 【移动到ui】里用到的
  const onSelect = useTocListOnSelect();
  const prefix = onSelect ? 'move' : ''; // for move ui
  const expandId = `${prefix}${groupType}_${uuid}${isLinkPage || isInLinkPage ? deep : ''}`;
  const IsLinkPageDelete = !block && isLinkPage;

  const expand = useExpandPageRecord(expandId);

  const fetchPage = useFetchPage(false);
  const position = $dropInfo((state) => {
    if (!state) return;
    if (!isAsideArea(state.cursorArea) || state.tocInfo?.dropTocType !== groupType) return;
    if (state.dropId === uuid) {
      return state.position;
    }
  });

  const ref = useRef<HTMLAnchorElement>(null);

  const openFilePreview = useOpenFilePreview();

  const { illegal } = usePermissions(block?.uuid || uuid);
  const readonly = useReadonly(uuid);

  const setExpand = (value: boolean) => {
    if (value) {
      setExpandPageRecord([expandId], true);
    } else {
      setExpandPageRecord([expandId], false);
    }
  };

  useEffect(() => {
    if (!expand) return;
    // 协同时会改变 toc 上 block 的 状态，目录展开且删除态时不请求页面
    if (block?.status !== BlockStatus.NORMAL) return;
    void fetchPage(block.uuid);
  }, [block?.uuid, block?.status, expand, fetchPage]);

  const sync = useGetSyncService();

  useEffect(() => {
    if (!block?.spaceId || !block.uuid || isLinkPage) return;
    // 同一个页面加一个节流，避免一个页面重复多次请求
    const fetchContent = throttle(fetchPage, 2000);
    return sync.subscribeChildPages(block.spaceId, block.uuid, () => {
      // 目录树里面的同步请求可能过多阻塞路由切换之后的数据请求，这里换个taskType
      void fetchContent(block.uuid, { taskType: 'sync' });
    });
  }, [uuid, sync, fetchPage, isLinkPage, block?.spaceId, block?.uuid]);

  // 不支持添加子页面
  const unSupportAddChild = Boolean(
    block &&
      [
        BlockType.COLLECTION_VIEW,
        BlockType.COLLECTION_VIEW_PAGE,
        BlockType.FILE,
        BlockType.MIND_MAPPING_PAGE,
        BlockType.MIND_MAPPING,
      ].includes(block.type)
  );

  // 被删除的引用页面需要显示无法访问
  // if (!block) return null

  const getSubNodes = () => {
    if (unSupportAddChild) {
      return null;
    }

    if (!expand) return null;

    if (block && subPages.length === 0 && !readonly && !isLinkPage && !illegal) {
      return <NewPage uuid={block.uuid} deep={deep + 1} />;
    }
    return subPages.map((item) => {
      return (
        <TocItem key={item} uuid={item} deep={deep + 1} isInLinkPage={isLinkPage || isInLinkPage} />
      );
    });
  };

  // 只要没数据或者没权限，就一律不显示到目录树
  const deleted = role === PermissionRole.NONE || block?.status === BlockStatus.DELETED_COMPLETE;

  const popup = useMemo(
    () =>
      illegal ? ILLEGAL_TEXT : segmentsToText(block?.data.segments) || getUntitledName(block?.type),
    [block?.data.segments, block?.type, illegal]
  );

  if (deleted) return null;
  if (block?.hidden) return null;

  return (
    <div data-block-id={uuid} key={uuid} data-toc-type={groupType} data-deep={deep}>
      <Tooltip className="relative block animate-fade-in" placement="right" popup={popup}>
        {position === 'top' && (
          <div className="absolute left-0 w-full top-[-1px] h-[2px] bg-black pointer-events-none" />
        )}
        <NavLink
          ref={ref}
          // to={(isLinkPage && !isLinkTable) ? linkPagePath : normalPath} // block 指的是真实的页面(block 是引用页面时是被引用的页面(引用多维表例外))
          to={linkPagePath} // 以前的不生效了,似乎现在全都是用这个链接才是正确的
          onContextMenu={openRightMenu}
          onClick={(e) => {
            if (IsLinkPageDelete) {
              e.preventDefault();
              return;
              // 如果是引用页面并且实际被引用的页面被彻底删除，不响应点击(like notion)
            }
            if (onSelect && block) {
              e.preventDefault();
              onSelect({
                uuid: block.uuid,
                type: block.type,
                data: block.data,
                parentId: block.parentId,
                title: segmentsToText(block.data.segments),
              });
              return;
            }
            bizTracker.event('page_click', {
              group_category: groupType,
              page_type: block?.type,
            });
            // 如果是收藏夹的文件，我们直接打开吧，要是进页面改动会比较大
            // 后面可能每个block都可以展示不同的ui
            if (block?.type === BlockType.FILE) {
              e.preventDefault();

              const text = segmentsToText(block.data.segments);
              const { extName } = getFileNameInfo(text);
              if (extName.toLowerCase() === 'pdf') {
                window.open(judgeSharePage() ? `/share/${block.uuid}` : `/preview/${block.uuid}`);
              } else {
                openFilePreview(block.uuid || uuid);
              }
            }

            const hasRightPage = getRightPageId();

            if (hasRightPage || e.altKey) {
              e.preventDefault();
              const openPageId = isLinkPage || !isLinkTable ? block?.uuid ?? uuid : uuid;

              openPage(openPageId, {
                forceOpenInRight: e.altKey,
                forceOpenNewTab: e.ctrlKey || e.metaKey,
              });
            }
          }}
          {...bindDataTestId(TestIds.asideItemToc)}
          activeClassName={isLinkPage && !isLinkTable ? undefined : 'normal-bg'}
          className={cx(
            'rounded flex items-center relative h-8 border-transparent group mx-2 border text-t2-medium text-grey2 space-x-1 pr-2',
            isDragging ? 'pointer-events-none' : 'animate-hover-black3',
            {
              'border-black': position === 'middle',
              'opacity-30': illegal,
            }
          )}
          style={{ paddingLeft: deep * 20 }}
        >
          <div className="w-1" />
          {block ? (
            <>
              {block ? (
                <PrefixIcon
                  className={'hidden group-hover:flex animate-hover animate-click'}
                  unSupportAddChild={unSupportAddChild}
                  expand={expand}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setExpand(!expand);
                    bizTracker.event(expand ? 'pagefold_click' : 'pageunfold_click', {
                      group_category: groupType,
                    });
                  }}
                />
              ) : (
                <div className="w-5 h-5 flex-shrink-0" />
              )}

              <IconTrigger
                trigger={false}
                iconSize={18}
                blockId={isLinkTable ? uuid : block?.uuid ?? ''}
                hasOverlay={isLinkPage}
                popcorn={ref.current}
                className={'w-5 h-5 text-grey3 group-hover:hidden'}
                placement="bottom-start"
                defaultIcon={<BlockDefaultIcon uuid={block?.uuid} size="middle" />}
              />

              <div className="block flex-1 pl-1 text-ellipsis">
                {illegal ? ILLEGAL_TEXT : <SegmentPlainText uuid={uuid} />}
              </div>
            </>
          ) : (
            // 引用的页面被彻底删除时显示无权限访问
            <NoAccess id={uuid} disable={true} className="w-full" />
          )}

          <div className={'flex items-center justify-center space-x-1'}>
            <MoreButton uuid={uuid} deep={deep} />
            {!readonly &&
              block &&
              !illegal &&
              block.type !== BlockType.MIND_MAPPING_PAGE &&
              block.type !== BlockType.FILE &&
              block.type !== BlockType.EXTERNAL_FILE && (
                <AddButton openTocItem={() => setExpand(true)} parentId={uuid} />
              )}
          </div>
        </NavLink>

        {position === 'bottom' && (
          <div className="absolute w-full left-0 bottom-[-1px] h-[2px] bg-black pointer-events-none" />
        )}
      </Tooltip>
      {getSubNodes()}
    </div>
  );
};

const PrefixIcon: FC<{
  unSupportAddChild: boolean;
  expand: boolean;
  className: string;
  onClick: (e: React.MouseEvent) => void;
}> = (props) => {
  const { className, unSupportAddChild, expand, onClick } = props;
  if (unSupportAddChild) {
    return (
      <span className={cx('flex items-center justify-center w-5 h-5', className)}>
        <Icon name="IcNaviDot" size="xxsmall" className="text-grey3" />
      </span>
    );
  }
  return (
    <button onClick={onClick} className={cx('flex items-center justify-center w-5 h-5', className)}>
      <IconUiSizeBox>
        <Icon
          name="IcArrowRight01"
          size="xxsmall"
          className={cx('text-grey3 transition-all', { 'rotate-90': expand })}
        />
      </IconUiSizeBox>
    </button>
  );
};

/**
 * 优化,只pick toc要用到的数据，避免重复渲染
 * 如果是引用page的话，会返回ref指向的真正的block
 */
const useTocItemBlock = (uuid: string) => {
  return useObservableStore(
    ({ blocks }) => {
      const block = blocks[uuid];
      const isLinkPage = isLinkPageBlock(block);
      const reallyBlock = isLinkPage ? blocks[block?.data.ref?.uuid ?? ''] : block;
      if (!reallyBlock) return;
      return pick(reallyBlock, ['status', 'type', 'spaceId', 'uuid', 'hidden', 'data', 'parentId']);
    },
    [uuid]
  );
};

export interface SegmentPlainTextProps {
  uuid: string;
  deep?: number;
  className?: string;
}
